// @ts-check
import React, { StatelessComponent } from 'react'; // eslint-disable-line no-unused-vars
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import _ from 'lodash';
import ProfileImage from 'shared/components/ProfileImage';
import LocationInfoCard from '../LocationInfoCard';
import GroupStatusIndicator from '../GroupStatusIndicator';
import AvailableSlotsPicker from '../AvailableSlotsPicker';
import NoSlotsAvailablePicker from '../NoSlotsAvailablePicker';
/* eslint-disable no-unused-vars */
import { ProviderMappingResult, Provider, ReferralProfile, AppointmentSlot } from '../types';
/* eslint-enable no-unused-vars */
import classes from './ProviderModal.module.scss';

/** @typedef {{
  matchingResult: ProviderMappingResult,
  onSlotClick: (slot: AppointmentSlot) => void,
  onProviderClick: (result: ProviderMappingResult) => void
}} LocationResultPropsType */

const locationResultPropTypes = {
  matchingResult: PropTypes.object.isRequired,
  onSlotClick: PropTypes.func.isRequired,
  onProviderClick: PropTypes.func.isRequired,
};

/** @type { StatelessComponent<LocationResultPropsType> } */
const LocationResult = ({ matchingResult, onSlotClick, onProviderClick }) => {
  const { location, distance, earliestAppointmentTime } = matchingResult;
  const hasSlots = !!earliestAppointmentTime;

  return (
    <div className={classes.LocationResult}>
      <LocationInfoCard
        location={location}
        distance={distance}
        acceptingNewPatients={matchingResult.acceptingNewPatients}
      />

      <div className={classes.slot_picker_container}>
        {hasSlots ? (
          <AvailableSlotsPicker matchingResult={matchingResult} onSlotClick={onSlotClick} />
        ) : (
          <NoSlotsAvailablePicker
            matchingResult={matchingResult}
            onProviderClick={onProviderClick}
          />
        )}
      </div>
    </div>
  );
};

LocationResult.displayName = 'LocationResult';
LocationResult.propTypes = locationResultPropTypes;

/** @typedef {{
  referralProfile: ReferralProfile,
  provider: Provider,
  matchingResults: ProviderMappingResult[],
  onSlotClick: (slot: AppointmentSlot) => void,
  onProviderClick: (result: ProviderMappingResult) => void
}} ProviderReferralModalPropsType */

/**
 * @todo: these ought to be required, but don't exist when modal is not open
 * @type { React.ValidationMap<ProviderReferralModalPropsType> }
 * */
const propTypes = {
  referralProfile: PropTypes.object,
  provider: PropTypes.object,
  matchingResults: PropTypes.array,
  onSlotClick: PropTypes.func,
  onProviderClick: PropTypes.func,
};

/** @type { StatelessComponent<ProviderReferralModalPropsType> } */
const ProviderReferralModal = ({
  referralProfile,
  provider,
  matchingResults,
  onSlotClick,
  onProviderClick,
}) => (
  <div className={classes.ProviderModal}>
    <div className={classes.heading}>
      <div className={classes.image_container}>
        <ProfileImage src={provider.profileImagePath} size={110} />
      </div>
      <div className={classes.info}>
        <div>
          <h3>{provider.nameWithAppellation}</h3>
          <h4>{(provider.specialtyNames || []).join(', ')}</h4>
          {/* TODO: Probably encapsulate this logic in its own component */}
          <GroupStatusIndicator
            enteredByGuest={!!referralProfile.enteredByGuest}
            enteredByReceiver={!!referralProfile.enteredByReceiver}
            isVhan={!!referralProfile.isVhan}
            hasGroup={referralProfile.hasGroup}
            isInGroup={provider.isInGroup}
          />
        </div>
        {/* TODO: #1741 Display insurance acceptance information. https://github.com/Preferral/Preferral/issues/1741 */}
        {/* <div className="section">
          { provider.acceptsPatientsInsurance ? "Accepts Patient's Insurance" : "Does not accept patient's insurance" }
        </div>*/}
      </div>
    </div>
    <div>
      {matchingResults.map(r => (
        <LocationResult
          key={r.id}
          matchingResult={r}
          onSlotClick={onSlotClick}
          onProviderClick={onProviderClick}
        />
      ))}
    </div>
  </div>
);

ProviderReferralModal.displayName = 'ProviderReferralModal';
ProviderReferralModal.propTypes = propTypes;

const mapStateToProps = (state, existingProps) => {
  const { pageState, entities } = state.referralDashboardResults;
  const providerId = existingProps.providerId;
  const resultsWithLocations = _.values(entities.matchingResults)
    .filter(r => r.provider === providerId)
    .map(r => ({
      ...r,
      location: entities.locations[r.location],
    }));

  return {
    provider: entities.providers[providerId],
    referralProfile: entities.referralProfiles[pageState.referralProfileId],
    matchingResults: resultsWithLocations,
  };
};

export default connect(mapStateToProps)(ProviderReferralModal);
